/**
 * Product Availability Utilities
 * 
 * Funzioni per calcolare la disponibilità dei prodotti e ordinarli
 * in modo che i prodotti non disponibili (stock 0) siano sempre in fondo.
 */

/**
 * Calcola lo stock totale di un prodotto considerando varianti e timeline.
 * 
 * @param {Object} product - Il prodotto da analizzare
 * @returns {Object} { isAvailable: boolean, totalStock: number, isOnDemand: boolean }
 * 
 * isAvailable: true se il prodotto è acquistabile (stock > 0 o su commissione)
 * totalStock: quantità totale a magazzino (somma di tutte le varianti)
 * isOnDemand: true se il prodotto è "su commissione" (non immediate)
 */
export const getProductAvailability = (product) => {
  if (!product) return { isAvailable: false, totalStock: 0, isOnDemand: false };

  const customOptions = product.customization_options || {};
  const timeline = customOptions.timeline || 'immediate';
  const hasVariants = customOptions.hasVariants && customOptions.variants?.length > 0;
  const variantSettings = customOptions.variantSettings || {};
  const legacyInventory = customOptions.inventory || {};

  // Se il prodotto è "su commissione" (non immediate), è sempre disponibile
  const isOnDemand = timeline !== 'immediate';
  
  if (isOnDemand) {
    return { isAvailable: true, totalStock: Infinity, isOnDemand: true };
  }

  // Calcola lo stock totale per prodotti con disponibilità immediata
  let totalStock = 0;

  if (hasVariants) {
    // Prodotto con varianti: somma lo stock di tutte le combinazioni
    const variants = customOptions.variants || [];
    
    // Genera tutte le combinazioni di varianti
    const generateCombinations = (variants) => {
      if (variants.length === 0) return [[]];
      const [first, ...rest] = variants;
      const restCombinations = generateCombinations(rest);
      const combinations = [];
      for (const value of first.values || []) {
        for (const restCombo of restCombinations) {
          combinations.push([{ type: first.type, value }, ...restCombo]);
        }
      }
      return combinations;
    };

    const combinations = generateCombinations(variants);
    
    for (const combo of combinations) {
      const key = combo.map(c => `${c.type}:${c.value}`).join('|');
      
      // Prima controlla variantSettings, poi fallback a legacy inventory
      if (variantSettings[key]) {
        const settings = variantSettings[key];
        // Se questa variante è su commissione, conta come disponibile
        if (settings.timeline && settings.timeline !== 'immediate') {
          totalStock += 1000; // Valore alto per indicare disponibilità
        } else {
          totalStock += settings.stock ?? 0;
        }
      } else if (legacyInventory[key] !== undefined) {
        totalStock += legacyInventory[key];
      }
    }
  } else {
    // Prodotto senza varianti: usa 'default' come chiave
    if (variantSettings['default']) {
      const settings = variantSettings['default'];
      if (settings.timeline && settings.timeline !== 'immediate') {
        totalStock = Infinity;
      } else {
        totalStock = settings.stock ?? 0;
      }
    } else if (legacyInventory['default'] !== undefined) {
      totalStock = legacyInventory['default'];
    } else {
      // Fallback: se non c'è inventory configurato, considera disponibile
      // (questo gestisce prodotti vecchi o mal configurati)
      totalStock = 0;
    }
  }

  return {
    isAvailable: totalStock > 0,
    totalStock: totalStock,
    isOnDemand: false
  };
};

/**
 * Ordina un array di prodotti mettendo quelli non disponibili in fondo.
 * Mantiene l'ordine originale per i prodotti disponibili e per quelli non disponibili.
 * 
 * @param {Array} products - Array di prodotti da ordinare
 * @param {Function} [secondarySort] - Funzione di ordinamento secondaria opzionale
 * @returns {Array} Array ordinato
 */
export const sortProductsByAvailability = (products, secondarySort = null) => {
  if (!products || !Array.isArray(products)) return [];

  // Separa i prodotti disponibili da quelli non disponibili
  const available = [];
  const unavailable = [];

  for (const product of products) {
    const { isAvailable } = getProductAvailability(product);
    if (isAvailable) {
      available.push(product);
    } else {
      unavailable.push(product);
    }
  }

  // Applica l'ordinamento secondario se fornito
  if (secondarySort && typeof secondarySort === 'function') {
    available.sort(secondarySort);
    unavailable.sort(secondarySort);
  }

  // Ritorna prima i disponibili, poi i non disponibili
  return [...available, ...unavailable];
};

/**
 * Crea una funzione di comparazione che mette i prodotti non disponibili in fondo
 * e usa un comparatore secondario per gli altri.
 * 
 * @param {Function} secondaryComparator - Funzione di comparazione secondaria (a, b) => number
 * @returns {Function} Funzione di comparazione per Array.sort()
 */
export const createAvailabilityComparator = (secondaryComparator = null) => {
  return (a, b) => {
    const availA = getProductAvailability(a);
    const availB = getProductAvailability(b);

    // Prima regola: disponibili prima di non disponibili
    if (availA.isAvailable && !availB.isAvailable) return -1;
    if (!availA.isAvailable && availB.isAvailable) return 1;

    // Se entrambi hanno la stessa disponibilità, usa il comparatore secondario
    if (secondaryComparator && typeof secondaryComparator === 'function') {
      return secondaryComparator(a, b);
    }

    return 0;
  };
};

export default {
  getProductAvailability,
  sortProductsByAvailability,
  createAvailabilityComparator
};
